import { useState, useEffect } from 'react'
import { Car, Zap, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { leadsAPI, bilerSearchToParams } from '../api'
import type { OrgVehicle, OrgLeasingselskap, BilerSearchFilters } from '../api'
import { formatNorwegianDate, formatDrivstoff } from '../../../utils/dateUtils'
import EierhistorikkKnapp from '../eierhistorikk/EierhistorikkKnapp'
import { useKanSeEierhistorikk } from '../eierhistorikk/tilgang'
import { useLatest } from '../../Bruktbil/useLatest'
import LeadsTableExport from '../LeadsTableExport'
import { LEADS_EXPORT_LIMIT } from '../eksportTilgang'
import type { ColumnDefinition } from '../../../utils/exportToCsv'

interface Props {
  orgNr: string
  /** Shown as the current owner in the eierhistorikk panel. For a leased car
   *  the registered owner is the leasing company, not this org. */
  orgNavn?: string | null
  vehicleClass?: string
  /** The Biler search the user arrived with (#625). Its matching cars sort
   *  first and get the highlight treatment, so the dealer sees the cars the
   *  tab counted on top — the rest of the fleet is still listed below. */
  bilerSearch?: BilerSearchFilters | null
}

const PAGE_SIZE = 50
const VEHICLE_CLASS_LABEL: Record<string, 'common.personbil' | 'common.varebil'> = { personbil: 'common.personbil', varebil: 'common.varebil' }

/** Columns the backend accepts as sort_by — sorting is server-side because
 *  the table is server-paginated. */
type SortKey =
  | 'kjennemerke' | 'merke' | 'modell'
  | 'registrert_forste_gang' | 'registrert_forste_gang_pa_eier'
  | 'vehicle_class' | 'drivstofftype' | 'leasing_company' | 'has_active_lead'

interface VehicleQuery {
  vehicleClass: string; merke: string; leasing: string; leasingCompany: string
  sortBy: SortKey; sortDir: 'asc' | 'desc'
}

/** The grid's filters and sort, for one page or for the export (#885). */
function vehicleParams(q: VehicleQuery, limit: number, offset: number) {
  return {
    vehicle_class: q.vehicleClass || undefined,
    merke: q.merke || undefined,
    leasing: q.leasing === '' ? undefined : q.leasing === 'leaset',
    leasing_company: q.leasingCompany || undefined,
    sort_by: q.sortBy,
    sort_dir: q.sortDir,
    limit,
    offset,
  }
}

const exportDate = (v: string | null | undefined) => (v ? formatNorwegianDate(v) : '—')

export default function VehiclesSection({ orgNr, orgNavn, vehicleClass, bilerSearch }: Props) {
  const { t } = useTranslation('leads')
  const kanSeHistorikk = useKanSeEierhistorikk()
  const [vehicles, setVehicles] = useState<OrgVehicle[]>([])
  const [total, setTotal] = useState(0)
  const [totalCapped, setTotalCapped] = useState(false)
  const [matchTotal, setMatchTotal] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [localVehicleClass, setLocalVehicleClass] = useState(vehicleClass ?? '')
  const [merkeFilter, setMerkeFilter] = useState('')
  const [merkeOptions, setMerkeOptions] = useState<{ merke: string; car_count: number }[]>([])
  const [leasingFilter, setLeasingFilter] = useState('')
  const [leasingCompanyFilter, setLeasingCompanyFilter] = useState('')
  const [leasingCompanyOptions, setLeasingCompanyOptions] = useState<OrgLeasingselskap[]>([])
  const [sortBy, setSortBy] = useState<SortKey>('registrert_forste_gang_pa_eier')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  // The merke filter is a dropdown of the org's actual merker (#335) — raw
  // SVV strings for now, normalisation is #318.
  useEffect(() => {
    leadsAPI.getOrgVehicleMerker(orgNr)
      .then(res => setMerkeOptions(res.data.items))
      .catch(() => {})
  }, [orgNr])

  // Feeds both the leasingselskap dropdown and the summary line above the grid
  useEffect(() => {
    leadsAPI.getOrgVehicleLeasingselskap(orgNr)
      .then(res => setLeasingCompanyOptions(res.data.items))
      .catch(() => {})
  }, [orgNr])

  // primitive key for the fetch deps — the search object is rebuilt per render
  const matchKey = bilerSearch ? bilerSearchToParams(bilerSearch).toString() : ''

  // Reset page when any filter or the sort changes
  useEffect(() => { setPage(1) }, [localVehicleClass, merkeFilter, leasingFilter, leasingCompanyFilter, sortBy, sortDir, matchKey])

  useLatest(alive => {
    setLoading(true)
    leadsAPI.getOrgVehicles(orgNr, vehicleParams({
      vehicleClass: localVehicleClass, merke: merkeFilter, leasing: leasingFilter,
      leasingCompany: leasingCompanyFilter, sortBy, sortDir,
    }, PAGE_SIZE, (page - 1) * PAGE_SIZE), bilerSearch)
      .then(res => {
        if (!alive()) return
        setVehicles(res.data.items)
        setTotal(res.data.total)
        setTotalCapped(res.data.total_is_capped)
        setMatchTotal(res.data.match_total ?? null)
      })
      .catch(() => {})
      .finally(() => { if (alive()) setLoading(false) })
  }, [orgNr, page, localVehicleClass, merkeFilter, leasingFilter, leasingCompanyFilter, sortBy, sortDir, matchKey])

  function handleSort(key: SortKey) {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      // leasing_company sorts desc first: owned cars have no company name, and
      // MySQL puts those NULLs first on ASC — desc keeps the names at the top
      const descFirst = key.startsWith('registrert') || key === 'leasing_company' || key === 'has_active_lead'
      setSortDir(descFirst ? 'desc' : 'asc')
    }
  }

  function handleNullstill() {
    setMerkeFilter('')
    setLocalVehicleClass('')
    setLeasingFilter('')
    setLeasingCompanyFilter('')
    setSortBy('registrert_forste_gang_pa_eier')
    setSortDir('desc')
  }

  const filtersActive = !!(merkeFilter || localVehicleClass || leasingFilter || leasingCompanyFilter
    || sortBy !== 'registrert_forste_gang_pa_eier' || sortDir !== 'desc')

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const exportColumns: ColumnDefinition[] = [
    { key: 'kjennemerke', label: t('common.kjennemerke') },
    { key: 'merke', label: t('common.merke'), format: v => v ?? '—' },
    { key: 'modell', label: t('common.modell'), format: v => v ?? '—' },
    { key: 'vehicle_class', label: t('common.type'), format: v => (v && VEHICLE_CLASS_LABEL[v] ? t(VEHICLE_CLASS_LABEL[v]) : v ?? '—') },
    { key: 'drivstofftype', label: t('vehicles.drivstoff'), format: v => formatDrivstoff(v) },
    { key: 'registrert_forste_gang', label: t('common.firstReg'), format: exportDate },
    { key: 'registrert_forste_gang_pa_eier', label: t('common.regPaEier'), format: exportDate },
    { key: 'leasing_company_navn', label: t('vehicles.leasing'), format: v => v ?? '—' },
    { key: 'has_active_lead', label: t('vehicles.lead'), format: v => (v ? t('mineLeads.yes') : t('mineLeads.no')) },
  ]

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          <Car className="w-5 h-5 inline mr-2" />{t('vehicles.title')}
        </h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('common.totalCount', { n: total })}
          {matchTotal !== null && matchTotal > 0 && (
            <span data-testid="vehicles-match-badge" className="ml-2 inline-flex items-center px-2 py-0.5 rounded-sm text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
              {t('vehicles.matchBadge', { n: matchTotal })}
            </span>
          )}
        </span>
      </div>

      {/* Who the dealer competes against, without paging the grid. The facet is
          ordered by car_count DESC, so slice(0, 3) is the top three. */}
      {leasingCompanyOptions.length > 0 && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          <span className="font-medium">{t('vehicles.leasingselskap')}</span>{' '}
          {leasingCompanyOptions.slice(0, 3)
            .map(l => `${l.navn} (${l.car_count})`)
            .join(' · ')}
          {leasingCompanyOptions.length > 3 && ` · ${t('vehicles.more', { n: leasingCompanyOptions.length - 3 })}`}
        </p>
      )}

      {/* One horizontal filter row (#335) */}
      <div className="flex flex-nowrap items-center gap-3 mb-4 overflow-x-auto">
        <select
          value={merkeFilter}
          onChange={e => setMerkeFilter(e.target.value)}
          className="input-field text-sm"
        >
          <option value="">{t('common.allMerker')}</option>
          {merkeOptions.map(m => (
            <option key={m.merke} value={m.merke}>{m.merke} ({m.car_count})</option>
          ))}
        </select>
        <select
          value={localVehicleClass}
          onChange={e => setLocalVehicleClass(e.target.value)}
          className="input-field text-sm"
        >
          <option value="">{t('common.allTypes')}</option>
          <option value="personbil">{t('common.personbil')}</option>
          <option value="varebil">{t('common.varebil')}</option>
        </select>
        <select
          value={leasingFilter}
          onChange={e => setLeasingFilter(e.target.value)}
          className="input-field text-sm"
        >
          <option value="">{t('vehicles.eidOgLeaset')}</option>
          <option value="eid">{t('vehicles.kunEid')}</option>
          <option value="leaset">{t('vehicles.kunLeaset')}</option>
        </select>
        {leasingCompanyOptions.length > 0 && (
          <select
            value={leasingCompanyFilter}
            onChange={e => setLeasingCompanyFilter(e.target.value)}
            className="input-field text-sm"
          >
            <option value="">{t('vehicles.allLeasingselskap')}</option>
            {leasingCompanyOptions.map(l => (
              <option key={l.org_nr} value={l.org_nr}>{l.navn} ({l.car_count})</option>
            ))}
          </select>
        )}
        {filtersActive && (
          <button
            onClick={handleNullstill}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline whitespace-nowrap"
          >
            {t('common.reset')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
      ) : vehicles.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">{t('vehicles.empty')}</p>
      ) : (
        <>
          <LeadsTableExport
            rows={vehicles}
            columns={exportColumns}
            title={t('tableExport.orgTitle', { table: t('vehicles.title'), navn: orgNavn ?? orgNr, orgNr })}
            loadData={() => leadsAPI.getOrgVehicles(orgNr, vehicleParams({
              vehicleClass: localVehicleClass, merke: merkeFilter, leasing: leasingFilter,
              leasingCompany: leasingCompanyFilter, sortBy, sortDir,
            }, LEADS_EXPORT_LIMIT, 0), bilerSearch).then(res => res.data.items)}
            capped={totalCapped}
            testId="bedrift-vehicles-export"
          />
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <SortHeader label={t('common.kjennemerke')} sortKey="kjennemerke" current={sortBy} dir={sortDir} onSort={handleSort} />
                  <SortHeader label={t('common.merke')} sortKey="merke" current={sortBy} dir={sortDir} onSort={handleSort} />
                  <SortHeader label={t('common.modell')} sortKey="modell" current={sortBy} dir={sortDir} onSort={handleSort} />
                  <SortHeader label={t('common.type')} sortKey="vehicle_class" current={sortBy} dir={sortDir} onSort={handleSort} />
                  <SortHeader label={t('vehicles.drivstoff')} sortKey="drivstofftype" current={sortBy} dir={sortDir} onSort={handleSort} />
                  <SortHeader label={t('common.firstReg')} sortKey="registrert_forste_gang" current={sortBy} dir={sortDir} onSort={handleSort} />
                  <SortHeader label={t('common.regPaEier')} sortKey="registrert_forste_gang_pa_eier" current={sortBy} dir={sortDir} onSort={handleSort} />
                  <SortHeader label={t('vehicles.leasing')} sortKey="leasing_company" current={sortBy} dir={sortDir} onSort={handleSort} />
                  <SortHeader label={t('vehicles.lead')} sortKey="has_active_lead" current={sortBy} dir={sortDir} onSort={handleSort} />
                  {/* No column at all without the role, rather than an empty one. */}
                  {kanSeHistorikk && <th className="text-center whitespace-nowrap">{t('common.historikk')}</th>}
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v, idx) => (
                  <tr key={v.kjennemerke}
                      data-match={v.is_match ? 'true' : undefined}
                      className={`${v.is_match
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-primary-500'
                        : `${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} ${v.has_active_lead ? 'border-l-2 border-amber-400' : ''}`}`}>
                    <td className="font-mono text-xs text-primary-600 dark:text-primary-400">
                      {v.kjennemerke}
                      {/* registered on one of the org's bedriftsnumre (#1006) */}
                      {v.underenhet && (
                        <div className="font-sans text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap" data-testid="vehicle-underenhet">
                          {t('vehicles.registrertPaUnderenhet', { navn: v.underenhet.navn })}
                        </div>
                      )}
                    </td>
                    <td className="text-sm">{v.merke ?? '—'}</td>
                    <td className="text-sm">{v.modell ?? '—'}</td>
                    <td className="text-xs">{v.vehicle_class ? (VEHICLE_CLASS_LABEL[v.vehicle_class] ? t(VEHICLE_CLASS_LABEL[v.vehicle_class]) : v.vehicle_class) : '—'}</td>
                    <td className="text-xs text-gray-500">{formatDrivstoff(v.drivstofftype)}</td>
                    <td className="text-xs text-gray-500">{v.registrert_forste_gang ? formatNorwegianDate(v.registrert_forste_gang) : '—'}</td>
                    <td className="text-xs font-medium">{v.registrert_forste_gang_pa_eier ? formatNorwegianDate(v.registrert_forste_gang_pa_eier) : '—'}</td>
                    <td className="text-xs">{v.leasing_company_navn ?? '—'}</td>
                    <td className="text-center">
                      {v.has_active_lead && (
                        <Zap className="w-4 h-4 text-amber-500 inline" />
                      )}
                    </td>
                    {kanSeHistorikk && (
                      <td className="text-center">
                        <EierhistorikkKnapp
                          kjennemerke={v.kjennemerke}
                          kuid={v.kuid}
                          registrertPaEier={v.registrert_forste_gang_pa_eier}
                          navaerendeEier={
                            v.leasingbil ? v.leasing_company_navn : orgNavn
                          }
                          merke={v.merke}
                          modell={v.modell}
                          registrertForsteGang={v.registrert_forste_gang}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mt-4">
              <span>{t('common.pageOf', { page, pages })}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  {t('common.prev')}
                </button>
                <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function SortHeader({ label, sortKey, current, dir, onSort }: {
  label: string
  sortKey: SortKey
  current: SortKey
  dir: 'asc' | 'desc'
  onSort: (key: SortKey) => void
}) {
  const active = current === sortKey
  return (
    <th
      className="text-left whitespace-nowrap cursor-pointer select-none hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        ) : (
          <span className="w-3 h-3 inline-block" />
        )}
      </span>
    </th>
  )
}
